import { useEffect, useState } from "react";
import { api, formatApiError, fileUrl } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, PencilLine, FileText, Paperclip, DollarSign, CalendarClock, Send } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import AttachmentPicker from "../project/AttachmentPicker";
import CurrencySelect from "../CurrencySelect";
import { formatMoney } from "../../lib/currency";
import { useConfirm } from "../ConfirmProvider";
import { canSeeCosts } from "../../lib/services";
import { useAuth } from "../../context/AuthContext";

const PROPOSAL_STATUSES = ["Draft", "Sent", "Under Review", "Accepted", "Rejected", "Expired"];

const STATUS_COLORS = {
  "Draft": "bg-neutral-100 border-neutral-300 text-neutral-700",
  "Sent": "bg-blue-500/15 border-blue-500/30 text-blue-700",
  "Under Review": "bg-yellow-500/15 border-yellow-500/30 text-yellow-700",
  "Accepted": "bg-emerald-500/15 border-emerald-500/30 text-emerald-700",
  "Rejected": "bg-rose-500/15 border-rose-500/30 text-rose-700",
  "Expired": "bg-neutral-500/15 border-neutral-500/30 text-neutral-700",
};

export default function ProposalsPanel({ leadId, canEdit, canDelete, onChanged }) {
  const confirm = useConfirm();
  const [proposals, setProposals] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/leads/${leadId}/proposals`);
      setProposals(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [leadId]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/proposals/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Are you sure you want to delete this proposal?", description: "This cannot be undone." }))) return;
    try {
      await api.delete(`/proposals/${id}`);
      toast.success("Deleted");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="panel p-5" data-testid="proposals-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-orange-400">Proposals</div>
          <div className="text-sm text-neutral-600">{proposals.length} proposal{proposals.length===1?"":"s"} on this lead</div>
        </div>
        {canEdit && (
          <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-proposal-btn" size="sm" className="bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="w-4 h-4 mr-1" /> New Proposal
          </Button>
        )}
      </div>

      {loading ? <div className="text-neutral-500 text-sm">Loading…</div> :
       proposals.length === 0 ? <div className="text-sm text-neutral-500">No proposals yet.</div> :
       (
        <div className="space-y-2">
          {proposals.map(p => (
            <div key={p.id} className="rounded-lg border border-[#E4E7EC] bg-[#FFFFFF] p-4" data-testid={`proposal-row-${p.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <FileText className="w-4 h-4 text-orange-400" />
                    <span className="text-[10px] font-mono text-neutral-500">{p.proposal_id}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${STATUS_COLORS[p.status] || ""}`}>{p.status}</span>
                    {p.sent_at && <span className="text-[10px] font-mono text-neutral-500">Sent {new Date(p.sent_at).toLocaleDateString()}</span>}
                  </div>
                  <div className="font-semibold mt-1">{p.title}</div>
                  <div className="mt-1 flex items-center gap-4 text-[11px] font-mono text-neutral-600 flex-wrap">
                    {p.amount !== undefined && <span className="flex items-center gap-1 text-emerald-700" data-testid={`proposal-amount-${p.id}`}>{formatMoney(p.amount, p.currency)}</span>}
                    {p.valid_until && <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" />Valid until {p.valid_until}</span>}
                    {p.attachments?.length > 0 && <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" />{p.attachments.length} file{p.attachments.length===1?"":"s"}</span>}
                  </div>
                  {p.notes && <div className="mt-2 text-xs text-neutral-700 whitespace-pre-wrap line-clamp-2">{p.notes}</div>}
                  <div className="mt-2 space-y-1">
                    {(p.attachments || []).map((a, i) => (
                      <a key={i} href={fileUrl(a.path)} target="_blank" rel="noreferrer" className="text-[11px] font-mono flex items-center gap-2 text-orange-700 hover:text-orange-700 truncate" data-testid={`proposal-att-${p.id}-${i}`}>
                        <Paperclip className="w-3 h-3" /> {a.filename}
                      </a>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {canEdit && p.status === "Draft" && (
                    <Button size="sm" onClick={() => setStatus(p.id, "Sent")} className="bg-blue-500 hover:bg-blue-600 text-neutral-900" data-testid={`proposal-send-${p.id}`}>
                      <Send className="w-3.5 h-3.5 mr-1" /> Mark Sent
                    </Button>
                  )}
                  {canEdit && (
                    <select
                      className="text-[11px] font-mono bg-[#FFFFFF] border border-[#E4E7EC] rounded px-2 py-1"
                      value={p.status}
                      onChange={e => setStatus(p.id, e.target.value)}
                      data-testid={`proposal-status-select-${p.id}`}>
                      {PROPOSAL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  )}
                  {canEdit && (
                    <Button size="icon" variant="ghost" onClick={() => { setEdit(p); setOpen(true); }} data-testid={`proposal-edit-${p.id}`}>
                      <PencilLine className="w-4 h-4 text-neutral-600" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button size="icon" variant="ghost" onClick={() => remove(p.id)} data-testid={`proposal-delete-${p.id}`}>
                      <Trash2 className="w-4 h-4 text-rose-400" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ProposalForm open={open} onOpenChange={setOpen} initial={edit} leadId={leadId} onSaved={() => { load(); onChanged?.(); }} />
    </div>
  );
}

const empty = { title: "", amount: 0, currency: "USD", valid_until: "", status: "Draft", notes: "", attachments: [] };

function ProposalForm({ open, onOpenChange, initial, leadId, onSaved }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const showCosts = canSeeCosts(user);

  useEffect(() => { if (open) setForm(initial ? { ...empty, ...initial } : empty); }, [open, initial]);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (showCosts && !(Number(form.amount) > 0)) { toast.error("Amount must be greater than 0."); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (!showCosts) delete payload.amount;
      if (initial?.id) await api.put(`/proposals/${initial.id}`, payload);
      else await api.post(`/leads/${leadId}/proposals`, { ...payload, lead_id: leadId });
      toast.success(initial ? "Updated" : "Proposal added");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="proposal-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{initial ? "Edit Proposal" : "New Proposal"}</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">Draft the pitch. Send it. Watch the pipeline move.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <F label="Title" required span={2}><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="pff-title" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          {showCosts && <F label="Currency"><CurrencySelect value={form.currency} onChange={v => setForm({...form, currency: v})} testid="pff-currency"/></F>}
          {showCosts && <F label="Amount" required><Input type="number" min="0.01" step="any" value={form.amount} onChange={e => setForm({...form, amount: parseFloat(e.target.value) || 0})} data-testid="pff-amount" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>}
          <F label="Valid Until" span={2}><Input type="date" value={form.valid_until} onChange={e => setForm({...form, valid_until: e.target.value})} data-testid="pff-valid" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Status" span={2}>
            <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="pff-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{PROPOSAL_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Notes" span={2}><Textarea rows={4} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="pff-notes" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Attachments" span={2}><AttachmentPicker value={form.attachments} onChange={a => setForm({...form, attachments: a})} testid="pff-att" /></F>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="pff-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !form.title.trim()} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="pff-save">
            {busy ? "Saving..." : (initial ? "Save Changes" : "Add Proposal")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({label, required, children, span = 1}) {
  return (
    <div className={`space-y-1.5 ${span === 2 ? "col-span-2" : ""}`}>
      <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">
        {label}{required && <span className="text-rose-400 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
